import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AudioLines,
  CheckSquare,
  ExternalLink,
  FileText,
  Globe,
  ListChecks,
  Sparkles,
  Tag as TagIcon,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";
import type { Section } from "../../types/api";
import { AddToNotebookPopover } from "../notebooks/AddToNotebookPopover";
import type { AddToNotebookInput } from "../notebooks/useAddToNotebook";
import { AiPanel } from "../shell/AiPanel";

const SUMMARY_STYLES = [
  { id: "short", label: "Corto" },
  { id: "operational", label: "Operativo" },
  { id: "executive", label: "Ejecutivo" },
  { id: "onboarding", label: "Onboarding" },
  { id: "checklist", label: "Checklist" },
  { id: "risk", label: "Riesgo" },
] as const;

type SummaryStyle = (typeof SUMMARY_STYLES)[number]["id"];

const ENTITIES_MOCK = [
  { kind: "tech", label: "Cloudflare Workers" },
  { kind: "tech", label: "D1" },
  { kind: "tech", label: "R2" },
  { kind: "tech", label: "Tailscale" },
  { kind: "team", label: "Engineering" },
  { kind: "service", label: "1Password" },
];

const AUTOLINKS_MOCK = [
  { from: "Workers AI", to: "platform/workers-ai.md", confidence: 0.96 },
  { from: "Tailscale §2", to: "platform/tailscale-manual.md", confidence: 0.91 },
  { from: "wrangler.toml", to: "netbox/wrangler-config.md", confidence: 0.84 },
];

const RELATED_MOCK = [
  {
    kind: "guide",
    title: "Manual Tailscale para equipos",
    path: "platform/tailscale-manual.md",
    score: 0.94,
  },
  {
    kind: "guide",
    title: "Cloudflare Access — configuración",
    path: "platform/cf-access.md",
    score: 0.88,
  },
  {
    kind: "reference",
    title: "Gestión de secretos en 1Password",
    path: "platform/secrets-mgmt.md",
    score: 0.81,
  },
];

interface Props {
  docId: string;
  activeSecId?: string;
  sections: Section[];
}

/**
 * B4.6 + B4.7 — ViewerAiPanel reescrito a 4 tabs canónicos.
 *
 * Tab 1 Resumen     · 6 estilos pickeables + entidades detectadas.
 * Tab 2 Relacionados · documentos similares + autolinks aplicados.
 * Tab 3 Acciones    · grid 2x3 (resumir-doc, checklist, audio, briefing, traducir, preguntar).
 * Tab 4 Fuentes     · citadas en la sesión actual + placeholder.
 *
 * Datos reales se enchufan cuando los endpoints `/v2/docs/:id/{summary,related,entities,autolinks}`
 * estén disponibles. Mientras tanto mocks visibles y CTAs accionables.
 */
export function ViewerAiPanel({ docId, activeSecId, sections }: Props) {
  const active = sections.find((s) => s.id === activeSecId);
  const heading = active ? (active.heading_path[active.heading_path.length - 1] ?? "Doc") : "Doc";

  const item: AddToNotebookInput = active
    ? {
        item_type: "section",
        source_doc_id: docId,
        source_section_id: active.id,
        source_version_id: active.version_id,
        title: heading,
      }
    : { item_type: "doc", source_doc_id: docId, title: "Documento" };

  return (
    <AiPanel
      title={`Sección: ${heading}`}
      tabs={[
        {
          id: "summary",
          label: "Resumen",
          content: <SummaryTab heading={heading} docId={docId} sectionId={activeSecId} />,
        },
        {
          id: "related",
          label: "Relacionados",
          content: <RelatedTab docId={docId} />,
        },
        {
          id: "actions",
          label: "Acciones",
          content: <ActionsTab docId={docId} />,
        },
        {
          id: "sources",
          label: "Fuentes",
          content: <SourcesTab />,
        },
      ]}
      primaryAction={
        <AddToNotebookPopover
          item={item}
          trigger={
            <Button variant="primary" size="sm" className="w-full">
              📓 Añadir a apuntes
            </Button>
          }
          onAdded={(info) =>
            toast.success("Añadido a apuntes", { description: info.notebookTitle })
          }
        />
      }
    />
  );
}

function SummaryTab({
  heading,
  docId,
  sectionId,
}: {
  heading: string;
  docId: string;
  sectionId?: string;
}) {
  const [style, setStyle] = useState<SummaryStyle>("operational");
  const qc = useQueryClient();

  // C4 — fetch summary real; 404 = aún no generado
  const summary = useQuery({
    queryKey: ["doc-summary", docId, style],
    queryFn: async () => {
      try {
        return await api.getDocSummary(docId, { type: style, section_id: sectionId });
      } catch (err) {
        if ((err as { status?: number })?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!docId,
    staleTime: 60_000,
  });

  // C4 — entities reales (NER vía doc_mentions)
  const entities = useQuery({
    queryKey: ["doc-entities", docId],
    queryFn: () => api.getDocEntities(docId).catch(() => ({ items: [] })),
    enabled: !!docId,
    staleTime: 60_000,
  });

  const summarize = useMutation({
    mutationFn: () => api.summarizeDoc(docId, { type: style, section_id: sectionId }),
    onSuccess: () => {
      toast.success("Generación encolada — vuelve en unos segundos");
      window.setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ["doc-summary", docId, style] });
      }, 4000);
    },
    onError: (err) => toast.error(`No se pudo encolar: ${String(err)}`),
  });

  const liveEntities = entities.data?.items ?? [];
  const entitiesToShow =
    liveEntities.length > 0
      ? liveEntities.slice(0, 8).map((e) => ({ kind: e.kind, label: e.label }))
      : ENTITIES_MOCK;

  return (
    <div className="space-y-3" data-testid="ai-tab-summary">
      <div>
        <header className="mb-1.5 flex items-center justify-between text-[10px] uppercase tracking-wider text-(--text-tertiary)">
          <span>Estilo</span>
          {summary.data && (
            <span className="font-mono text-(--accent-600)" data-testid="ai-summary-cached">
              Cached · {summary.data.model}
            </span>
          )}
        </header>
        <div
          className="flex flex-wrap gap-1"
          data-testid="ai-summary-styles"
          aria-label="Estilo de resumen"
        >
          {SUMMARY_STYLES.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => setStyle(s.id)}
              data-testid={`ai-summary-style-${s.id}`}
              data-active={style === s.id ? "true" : "false"}
              aria-pressed={style === s.id}
              className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider transition ${
                style === s.id
                  ? "bg-(--accent-50) text-(--accent-700) border border-(--accent-200)"
                  : "bg-(--bg-elevated) text-(--text-secondary) border border-(--border-subtle) hover:bg-(--bg-hover)"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <article
        className="ai-summary-card rounded-md border border-(--accent-200) bg-(--accent-50) p-3 text-[13px] leading-relaxed text-(--text-primary)"
        data-testid="ai-summary-card"
      >
        {summary.isLoading && <p className="text-xs text-(--text-tertiary)">Cargando resumen…</p>}
        {!summary.isLoading && summary.data && (
          <>
            <p className="whitespace-pre-wrap" data-testid="ai-summary-text">
              {summary.data.content}
            </p>
            <footer className="mt-2 flex items-center gap-1.5 text-[10px] font-mono text-(--text-tertiary)">
              <Sparkles size={10} className="text-(--accent-500)" />
              <span>{summary.data.model}</span>
              {summary.data.confidence != null && (
                <span>· conf {summary.data.confidence.toFixed(2)}</span>
              )}
            </footer>
          </>
        )}
        {!summary.isLoading && !summary.data && (
          <div className="text-xs text-(--text-secondary)">
            <p className="mb-2">
              Aún no hay resumen <em>{style}</em> para "{heading}".
            </p>
            <Button
              size="sm"
              variant="primary"
              disabled={summarize.isPending}
              data-testid="ai-summary-generate"
              onClick={() => summarize.mutate()}
            >
              <Sparkles size={11} /> {summarize.isPending ? "Encolando…" : "Generar"}
            </Button>
          </div>
        )}
      </article>

      <section>
        <header className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-(--text-tertiary)">
          <span>Entidades detectadas</span>
          <span
            className="font-mono text-(--text-tertiary)"
            data-testid="ai-entities-source"
            data-live={liveEntities.length > 0 ? "true" : "false"}
          >
            {liveEntities.length > 0 ? `${liveEntities.length} reales` : "mock"}
          </span>
        </header>
        <div className="flex flex-wrap gap-1" data-testid="ai-entities">
          {entitiesToShow.map((e) => (
            <span
              key={e.label}
              className="inline-flex items-center gap-1 rounded-full bg-(--bg-elevated) border border-(--border-subtle) px-2 py-0.5 text-[11px]"
            >
              <span className="font-mono text-[9px] uppercase text-(--text-tertiary)">
                {e.kind}
              </span>
              {e.label}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}

function RelatedTab({ docId }: { docId: string }) {
  const live = useQuery({
    queryKey: ["doc-related", docId],
    queryFn: () => api.listDocRelated(docId).catch(() => ({ items: [] })),
    enabled: !!docId,
    staleTime: 60_000,
  });
  const liveItems = live.data?.items ?? [];
  const usingLive = liveItems.length > 0;
  const items = usingLive
    ? liveItems
    : RELATED_MOCK.map((m) => ({
        doc_id: m.path,
        title: m.title,
        path: m.path,
        score: m.score,
        reason: "mock",
      }));
  return (
    <div className="space-y-4" data-testid="ai-tab-related">
      <section>
        <header className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-(--text-tertiary)">
          <span>Documentos relacionados</span>
          <span
            className="font-mono"
            data-testid="ai-related-source"
            data-live={usingLive ? "true" : "false"}
          >
            {usingLive ? "co-collection" : "mock"}
          </span>
        </header>
        <ul className="space-y-1" data-testid="ai-related-list">
          {items.map((r) => (
            <li
              key={r.doc_id}
              className="flex items-start gap-2 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2.5 py-1.5"
            >
              <FileText size={12} className="mt-0.5 text-(--text-tertiary)" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{r.title}</p>
                <p className="font-mono text-[10px] text-(--text-tertiary) truncate">{r.path}</p>
              </div>
              <Badge variant="muted" className="text-[9px]">
                {r.score.toFixed(2)}
              </Badge>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <header className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-(--text-tertiary)">
          <span>Autolinks aplicados</span>
          <span className="font-mono">render_link</span>
        </header>
        <ul className="space-y-1" data-testid="ai-autolinks-list">
          {AUTOLINKS_MOCK.map((a) => (
            <li
              key={a.from}
              className="flex items-center gap-2 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2.5 py-1.5"
            >
              <ExternalLink size={11} className="text-(--text-tertiary)" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px]">
                  <span className="text-(--accent-600) font-medium">{a.from}</span>
                  <span className="text-(--text-tertiary)"> → </span>
                  <span className="font-mono text-[10px]">{a.to}</span>
                </p>
              </div>
              <Badge variant="muted" className="text-[9px]">
                conf {a.confidence.toFixed(2)}
              </Badge>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ActionsTab({ docId: _docId }: { docId: string }) {
  return (
    <div className="space-y-3" data-testid="ai-tab-actions">
      <section>
        <header className="mb-1.5 text-[10px] uppercase tracking-wider text-(--text-tertiary)">
          Generar
        </header>
        <div className="grid grid-cols-2 gap-1.5" data-testid="ai-actions-grid">
          <ActionTile
            icon={<Sparkles size={14} />}
            title="Resumir doc"
            sub="6 estilos · cache"
            onClick={() => toast.message("Generando resumen del documento…")}
            testId="ai-action-summarize-doc"
          />
          <ActionTile
            icon={<CheckSquare size={14} />}
            title="Checklist"
            sub="desde sección"
            onClick={() => toast.message("Generando checklist…")}
            testId="ai-action-checklist"
          />
          <ActionTile
            icon={<AudioLines size={14} />}
            title="Audio overview"
            sub="5 min · TTS"
            onClick={() => toast.message("Generando audio overview…")}
            testId="ai-action-audio"
          />
          <ActionTile
            icon={<FileText size={14} />}
            title="Briefing"
            sub="ejecutivo"
            onClick={() => toast.message("Generando briefing…")}
            testId="ai-action-briefing"
          />
          <ActionTile
            icon={<Globe size={14} />}
            title="Traducir"
            sub="doc completo"
            onClick={() => toast.message("Traduciendo…")}
            testId="ai-action-translate"
          />
          <ActionTile
            icon={<ListChecks size={14} />}
            title="Extraer pasos"
            sub="estructurado"
            onClick={() => toast.message("Extrayendo pasos…")}
            testId="ai-action-extract-steps"
          />
        </div>
      </section>
    </div>
  );
}

function ActionTile({
  icon,
  title,
  sub,
  onClick,
  testId,
}: {
  icon: React.ReactNode;
  title: string;
  sub: string;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col items-start gap-1 rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-2.5 text-left transition hover:border-(--accent-300) hover:bg-(--bg-hover)"
    >
      <span className="text-(--accent-500)">{icon}</span>
      <span className="text-xs font-semibold text-(--text-primary)">{title}</span>
      <span className="font-mono text-[10px] text-(--text-tertiary)">{sub}</span>
    </button>
  );
}

function SourcesTab() {
  return (
    <div className="space-y-2" data-testid="ai-tab-sources">
      <header className="text-[10px] uppercase tracking-wider text-(--text-tertiary)">
        Fuentes citadas en esta sesión
      </header>
      <div className="rounded-md border border-dashed border-(--border-default) p-3 text-center text-xs text-(--text-tertiary)">
        <TagIcon size={14} className="mx-auto mb-1 opacity-50" />
        Las fuentes citadas aparecerán aquí cuando hagas preguntas o generes artefactos.
      </div>
    </div>
  );
}
